import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api from "../../../services/api";
import useProjectStore from "../../../store/useProjectStore";

function menuItems(isAdminOrSubadmin, onOpenFeedback, auth) {
  // A guest's account is an artefact of their access link — its email is where
  // their sign-in codes go, and they own no project. Both screens would only
  // hit a 403, so they are not offered.
  const isGuest = auth?.user?.role === "guest";
  return [
    !isGuest && {
      key: "edit-profile",
      label: "Edit Profile",
      path: "/profile",
      isActive: location.pathname === "/profile",
      icon: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.3" />
          <path d="M1.5 12.5c0-2.8 2.46-5 5.5-5s5.5 2.2 5.5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
      ),
    },
    isAdminOrSubadmin && {
      key: "admin-panel",
      label: auth.user?.role === "admin" ? "Admin Panel" : "Subadmin Panel",
      path: "/admin/users",
      isActive: location.pathname.startsWith("/admin"),
      accentColor: auth.user?.role === "admin" ? "#f59e0b" : "#a78bfa",
      icon: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <rect x="1" y="1" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.3" />
          <rect x="9" y="1" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.3" />
          <rect x="1" y="9" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.3" />
          <rect x="9" y="9" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      ),
    },
    !isGuest && {
      key: "project-settings",
      label: "Project Settings",
      path: "/settings",
      isActive: location.pathname === "/settings",
      icon: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <rect x="1" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" />
          <rect x="8" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" />
          <rect x="1" y="8" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" />
          <rect x="8" y="8" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      ),
    },
    {
      key: "feedback",
      label: "Feedback",
      isActive: false,
      onClick: () => onOpenFeedback?.(),
      icon: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M1.5 2.5h11v7h-6l-3 2.5v-2.5h-2v-7z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
        </svg>
      ),
    },
  ].filter(Boolean);
}

export default function UserMenu({ auth, isAdminOrSubadmin, onOpenFeedback, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  // closes the small dropdown AND the mobile sidebar
  const closeAll = () => {
    close();
    onClose?.();
  };

  // load project if store is empty
  const setProjects = useProjectStore((s) => s.setProjects);
  const projectCount = useProjectStore((s) => s.projects.length);

  useEffect(() => {
    if (projectCount > 0) return;
    api
      .getProjects().then((res) => setProjects(res.data.projects || []))
      .catch(() => { });
  }, []);

  const selectedProject = useProjectStore(
    (s) =>
      s.projects.find((p) => p.id === s.selectedProjectId) ||
      s.projects.find((p) => p.isDefault) ||
      s.projects[0]
  );
  const projectName = selectedProject?.name || "SaffronEdge";

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, close]);

  // Close on Escape, return focus to trigger
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        close();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, close]);

  // Close automatically whenever the route changes (menu item nav, back/forward, etc.)
  useEffect(() => {
    close();
  }, [location.pathname, close]);

  useLayoutEffect(() => {
    if (!open || !dropdownRef.current || !triggerRef.current) return;

    const dropdownEl = dropdownRef.current;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownEl.getBoundingClientRect().height;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    const upward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    if (upward) {
      dropdownEl.style.top = "auto";
      dropdownEl.style.bottom = "calc(100% + 6px)";
    } else {
      dropdownEl.style.bottom = "auto";
      dropdownEl.style.top = "calc(100% + 6px)";
    }
    dropdownEl.style.visibility = "visible";
  }, [open]);

  const handleToggle = () => setOpen((v) => !v);

  const displayName = auth.user?.fullName || auth.user?.username || "";
  const initial = displayName.split(" ").map((n) => n[0]).join("").toUpperCase();
  const capitalize = (s) =>
    s.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

  const roleColor =
    auth.user?.role === "admin"
      ? { color: "#f59e0b", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.3)" }
      : auth.user?.role === "subadmin"
        ? { color: "#a78bfa", bg: "rgba(167,139,250,0.12)", border: "rgba(167,139,250,0.3)" }
        : null;

  return (
    <div ref={wrapRef} className="wrapper__userDropdown pos__rel">
      {/* Trigger pill */}
      <button
        type="button"
        className={open ? "userDropdown__btn active" : "userDropdown__btn"}
        ref={triggerRef}
        onClick={handleToggle}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="username__initial">{initial}</span>
        <span className="userDetails mb__0 fc__5F636A fb__12">
          <span className="userName d-flex fb__16 fw__500 fc__0E1218 mb__4">
            {capitalize(displayName)}
          </span>
          <span className="wrapper__pro d-flex align-items-center mb__0 fb__12 fc__5F636A">
            <span className="wrapper__project fb__12 fc__5F636A">{projectName}</span>
            <span className="grayDot"></span> Workspace
          </span>
        </span>
        <span className="userMenu__accordion">
          <img src="/svg/usermenu_drop.svg" alt="Accordion icon" />
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="wrapper__dropdown__menu" ref={dropdownRef} role="menu">
          {/* User info header */}
          <div className="dropdwn__userInfo" style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
            <div className="dropdwn__userInfo__inner" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span className="username__initial">{initial}</span>
              <div className="infoDetails" style={{ flex: 1, minWidth: 0 }}>
                <div className="userSmall fb__13 fw__600 fc__0E1218 fc__ellipse">
                  {capitalize(displayName)}
                </div>
                <div className="userMail fb__12 fw__400 fc__5F636A fc__ellipse">
                  {auth.user?.email}
                </div>
              </div>
            </div>
            {roleColor && (
              <div className="userRole">
                <span
                  style={{
                    background: roleColor.bg,
                    border: `1px solid ${roleColor.border}`,
                    color: roleColor.color,
                  }}
                >
                  {auth.user?.role}
                </span>
              </div>
            )}
          </div>

          {/* Menu items */}
          <div className="dropdown__listing">
            {menuItems(isAdminOrSubadmin, onOpenFeedback, auth).map((item) => (
              <DropdownItem
                key={item.key}
                icon={item.icon}
                label={item.label}
                path={item.path}
                isActive={item.isActive}
                accentColor={item.accentColor}
                onClick={
                  item.onClick
                    ? () => { closeAll(); item.onClick(); }
                    : closeAll
                }
              />
            ))}

            <DropdownItem
              icon={
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5 2H3a1 1 0 00-1 1v8a1 1 0 001 1h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  <path d="M9 10l3-3-3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                  <line x1="12" y1="7" x2="5.5" y2="7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
              }
              label="Sign Out"
              accentColor="#f87171"
              onClick={() => { closeAll(); useProjectStore.getState().clearProjects(); auth.logout(); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function DropdownItem({ icon, label, onClick, path, isActive }) {
  const className = `dropdown__btn${isActive ? " dropdown__btn--active" : ""}`;
  const style = { flexShrink: 0 };

  const content = (
    <>
      <span style={{ opacity: 0.75, flexShrink: 0 }}>{icon}</span>
      {label}
    </>
  );

  // Internal route -> Link (client-side nav, no full reload).
  // No path (e.g. Feedback, Sign Out) -> plain button.
  if (path) {
    return (
      <Link to={path} className={className} onClick={onClick} role="menuitem">
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className={className} onClick={onClick} role="menuitem">
      {content}
    </button>
  );
}